import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { fetchContactsRequest } from '@/services/api/contact';
import { processAPIResponseNow } from '@/utils/request';
import { ContactModel } from '@/typings/models/Contact';
import { contactMatchesSearch, extractContactList, isContactOfCustomer } from '@/utils/contacts';

const SEARCH_DEBOUNCE_MS = 300;
/** `GET /contacts` ignores shorter terms. */
const MIN_SEARCH_LENGTH = 3;
const PAGE_SIZE = 20;
/** Fewer rows than this left after filtering (e.g. a page of the customer's own contacts): the next page loads by itself. */
const MIN_VISIBLE = 8;

type Directory = {
  /** The partner's contacts not linked to the excluded customer yet, as far as loaded. */
  contacts: ContactModel[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

const fetchPage = (search: string, page: number) =>
  new Promise<ContactModel[]>((resolve) => {
    fetchContactsRequest({
      query: { search: search || undefined, page, page_size: PAGE_SIZE, skip_total_size: 1 },
    }).then((response) =>
      processAPIResponseNow(
        response,
        {
          onSuccess: (body: unknown) => resolve(extractContactList(body).items),
          // Picking from the directory is a convenience; the customer's own contacts still show.
          onError: () => resolve([]),
        },
        'data',
      ),
    );
  });

/**
 * The partner's whole contact directory for picking, page by page: everyone
 * (most recent first) while nothing is typed, the server's matches from three
 * characters on. One or two characters filter what's loaded in place.
 */
export const useContactDirectory = (term: string, excludeCustomerId?: string): Directory => {
  const query = term.trim();
  const serverTerm = query.length >= MIN_SEARCH_LENGTH ? query : '';
  const [results, setResults] = useState<ContactModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const page = useRef(1);
  // Answers for an older term are dropped.
  const generation = useRef(0);

  const loadFirst = useMemo(
    () =>
      debounce((search: string, ticket: number) => {
        fetchPage(search, 1).then((items) => {
          if (ticket !== generation.current) return;
          page.current = 1;
          setResults(items);
          setHasMore(items.length === PAGE_SIZE);
          setLoading(false);
        });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    generation.current += 1;
    setLoading(true);
    loadFirst(serverTerm, generation.current);
  }, [serverTerm, loadFirst]);

  useEffect(() => () => loadFirst.cancel(), [loadFirst]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const ticket = generation.current;
    setLoading(true);
    fetchPage(serverTerm, page.current + 1).then((items) => {
      if (ticket !== generation.current) return;
      page.current += 1;
      setResults((loaded) => {
        const known = new Set(loaded.map((contact) => contact.id));
        return [...loaded, ...items.filter((contact) => !known.has(contact.id))];
      });
      setHasMore(items.length === PAGE_SIZE);
      setLoading(false);
    });
  }, [hasMore, loading, serverTerm]);

  const contacts = useMemo(
    () =>
      results.filter(
        (contact) =>
          !isContactOfCustomer(contact, excludeCustomerId) && (serverTerm || contactMatchesSearch(contact, query)),
      ),
    [results, excludeCustomerId, serverTerm, query],
  );

  // Filtering happens here, so a page can come out (nearly) empty with nothing on screen to scroll.
  useEffect(() => {
    if (!loading && hasMore && contacts.length < MIN_VISIBLE) loadMore();
  }, [loading, hasMore, contacts.length, loadMore]);

  return { contacts, loading, hasMore, loadMore };
};
